import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

const propTypes = {
  length: PropTypes.number,
  autofocus: PropTypes.bool,
  className: PropTypes.string,
  onChange: PropTypes.func,
  onComplete: PropTypes.func,
};

const SeparatedPinField = ({
  length = 4,
  autofocus,
  className,
  onChange,
  onComplete,
}) => {
  const [digits, setDigits] = useState(Array(length).fill(''));
  const refs = useRef([]);

  useEffect(() => {
    if (autofocus && refs.current[0]) {
      refs.current[0].focus();
    }
  }, [autofocus]);

  const handleChange = (index, value) => {
    const sanitized = value.replace(/\D/g, '');
    if (sanitized.length > 1) {
      const next = Array(length).fill('');
      sanitized
        .slice(0, length)
        .split('')
        .forEach((char, i) => {
          next[i] = char;
        });
      setDigits(next);
      const pin = next.join('');
      onChange?.(pin);
      if (pin.length === length) {
        onComplete?.(pin);
      }
      refs.current[Math.min(sanitized.length, length - 1)]?.focus();
      return;
    }
    if (!/^\d?$/.test(value)) return;
    const next = [...digits];
    next[index] = value;
    setDigits(next);
    const pin = next.join('');
    onChange?.(pin);
    if (value && index < length - 1) {
      refs.current[index + 1]?.focus();
    }
    if (pin.length === length && next.every(d => d !== '')) {
      onComplete?.(pin);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      refs.current[index - 1]?.focus();
    }
  };

  const handlePaste = e => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, length);
    const next = Array(length).fill('');
    pasted.split('').forEach((char, i) => {
      next[i] = char;
    });
    setDigits(next);
    const pin = next.join('');
    onChange?.(pin);
    if (pin.length === length) {
      onComplete?.(pin);
    }
    refs.current[Math.min(pasted.length, length - 1)]?.focus();
  };

  return (
    <div className={classNames('pin-field-separated', className)}>
      {digits.map((digit, i) => (
        <input
          key={i}
          ref={el => {
            refs.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          value={digit}
          className="pin-field-separated-input"
          onChange={e => handleChange(i, e.target.value)}
          onKeyDown={e => handleKeyDown(i, e)}
          onPaste={handlePaste}
        />
      ))}
    </div>
  );
};

SeparatedPinField.propTypes = propTypes;

export default SeparatedPinField;
